import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Skeleton,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Headphones as HeadphonesIcon,
  MenuBook as ReadingIcon,
  FactCheck as AttendanceIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as UndoneIcon,
  Cancel as NotCompletedIcon,
  Replay as ReplayIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useGetStudentExerciseStatisticsQuery } from "@/store/api/user/exerciseApi";
import ReviewAnswersDialog from "@/pages/user/Exercises/sections/ReviewAnswersDialog";

const sectionConfig = {
  "lc-exercise": {
    label: "LC",
    fullLabel: "Listening Comprehension",
    icon: <HeadphonesIcon sx={{ fontSize: 16 }} />,
    color: "#4facfe",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  "rc-exercise": {
    label: "RC",
    fullLabel: "Reading Comprehension",
    icon: <ReadingIcon sx={{ fontSize: 16 }} />,
    color: "#43e97b",
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  },
  "attendance-test": {
    label: "Test",
    fullLabel: "Attendance Test",
    icon: <AttendanceIcon sx={{ fontSize: 16 }} />,
    color: "#f093fb",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
};

const SECTION_ORDER = ["lc-exercise", "rc-exercise", "attendance-test"];

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const learningStatusConfig = {
  completed: { label: "Completed", color: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7", Icon: CheckCircleIcon },
  not_completed: { label: "Not Completed", color: "#ed6c02", bg: "#fff3e0", border: "#ffcc80", Icon: NotCompletedIcon },
  undone: { label: "Undone", color: "#9e9e9e", bg: "#f5f5f5", border: "#e0e0e0", Icon: UndoneIcon },
};

const SectionCell = ({ section, onReview }) => {
  if (!section) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
        N/A
      </Typography>
    );
  }

  const status = section.learningStatus || "undone";

  if (status === "undone") {
    return (
      <Chip
        icon={<UndoneIcon />}
        label="Undone"
        size="small"
        sx={{
          backgroundColor: "#f5f5f5",
          color: "#9e9e9e",
          fontWeight: 600,
          "& .MuiChip-icon": { color: "#9e9e9e" },
        }}
      />
    );
  }

  const bestPct = section.bestPercentage ?? 0;
  const latestPct = section.latestPercentage ?? 0;
  const attempts = section.totalAttempts ?? 0;
  const isLC = section.sectionType === "lc-exercise";
  const cfg = learningStatusConfig[status] || learningStatusConfig.not_completed;
  const hasReview = !!section.latestSessionId;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" display="block" fontWeight={600}>
            Best: {Math.round(bestPct)}% | Latest: {Math.round(latestPct)}%
          </Typography>
          <Typography variant="caption" display="block">
            {section.correctAnswers ?? "?"}/{section.totalQuestions ?? "?"} correct
          </Typography>
          <Typography variant="caption" display="block">
            Attempts: {attempts}{isLC ? "/3 required" : ""}
          </Typography>
          <Typography variant="caption" display="block">
            {formatDate(section.lastCompletedAt)}
          </Typography>
        </Box>
      }
      arrow
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
        <Chip
          icon={<cfg.Icon />}
          label={`${Math.round(bestPct)}%`}
          size="small"
          sx={{
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
            fontWeight: 700,
            fontSize: "0.8rem",
            "& .MuiChip-icon": { color: cfg.color, fontSize: 16 },
          }}
        />
        {isLC && status === "not_completed" && attempts < 3 && (
          <Typography variant="caption" sx={{ color: "#ed6c02", fontSize: "0.65rem", fontWeight: 600 }}>
            <ReplayIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.3 }} />
            {attempts}/3 attempts
          </Typography>
        )}
        {hasReview && (
          <Chip
            icon={<VisibilityIcon />}
            label="Review"
            size="small"
            variant="outlined"
            onClick={() => onReview(section.latestSessionId)}
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              cursor: "pointer",
              color: "#1976d2",
              borderColor: "#90caf9",
              "& .MuiChip-icon": { fontSize: 14, color: "#1976d2" },
              "&:hover": {
                backgroundColor: "#e3f2fd",
                borderColor: "#1976d2",
              },
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const ExerciseStatistics = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useGetStudentExerciseStatisticsQuery();
  const [reviewSessionId, setReviewSessionId] = useState(null);

  const statistics = data?.data || [];

  // Compute overview stats
  const totalSections = statistics.reduce((sum, day) => sum + day.sections.length, 0);
  const completedSections = statistics.reduce(
    (sum, day) => sum + day.sections.filter((s) => s.learningStatus === "completed").length,
    0
  );
  const notCompletedSections = statistics.reduce(
    (sum, day) => sum + day.sections.filter((s) => s.learningStatus === "not_completed").length,
    0
  );
  const undoneSections = totalSections - completedSections - notCompletedSections;
  const attemptedSections = completedSections + notCompletedSections;
  const averagePercentage =
    attemptedSections > 0
      ? statistics.reduce(
          (sum, day) =>
            sum +
            day.sections
              .filter((s) => s.learningStatus !== "undone")
              .reduce((s, sec) => s + (sec.bestPercentage || 0), 0),
          0
        ) / attemptedSections
      : 0;

  // Group by exercise
  const groupedByExercise = {};
  for (const day of statistics) {
    const exId = day.exerciseId?.toString() || "unknown";
    if (!groupedByExercise[exId]) {
      groupedByExercise[exId] = {
        exerciseTitle: day.exerciseTitle || "Exercise",
        days: [],
      };
    }
    groupedByExercise[exId].days.push(day);
  }

  // Helper: find section by type within a day
  const getSection = (day, sectionType) =>
    day.sections.find((s) => s.sectionType === sectionType) || null;

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load exercise statistics. Please try again later.
      </Alert>
    );
  }

  const overviewCardSx = (gradient) => ({
    p: { xs: 1.5, sm: 2.5 },
    textAlign: "center",
    background: gradient,
    color: "white",
    borderRadius: 2,
  });
  const overviewValueSx = {
    fontWeight: "bold",
    fontSize: { xs: "1.75rem", sm: "3rem" },
    lineHeight: 1.2,
  };
  const overviewLabelSx = { fontSize: { xs: "0.75rem", sm: "0.875rem" } };

  return (
    <Box>
      {/* Overview Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid item xs={4} sm={4}>
          <Card sx={overviewCardSx("linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)")}>
            <Typography sx={overviewValueSx}>{completedSections}</Typography>
            <Typography sx={overviewLabelSx}>Completed</Typography>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4}>
          <Card sx={overviewCardSx("linear-gradient(135deg, #f093fb 0%, #f5576c 100%)")}>
            <Typography sx={overviewValueSx}>{notCompletedSections}</Typography>
            <Typography sx={overviewLabelSx}>Not Completed</Typography>
          </Card>
        </Grid>
        <Grid item xs={4} sm={4}>
          <Card sx={overviewCardSx("linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)")}>
            <Typography sx={overviewValueSx}>{Math.round(averagePercentage)}%</Typography>
            <Typography sx={overviewLabelSx}>Average Score</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics Table per Exercise */}
      {statistics.length === 0 ? (
        <Card sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <AssessmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No exercise data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete some exercises to see your statistics here.
          </Typography>
        </Card>
      ) : (
        Object.entries(groupedByExercise).map(([exId, group]) => (
          <Card key={exId} sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: { xs: "12px !important", sm: "16px !important" } }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  wordBreak: "break-word",
                }}
              >
                {group.exerciseTitle}
              </Typography>

              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", minWidth: { xs: 520, sm: 600 } }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 700, width: { xs: 160, sm: 220 }, px: { xs: 1, sm: 2 } }}>
                        Day
                      </TableCell>
                      {SECTION_ORDER.map((type) => {
                        const cfg = sectionConfig[type];
                        return (
                          <TableCell
                            key={type}
                            align="center"
                            sx={{ fontWeight: 700, width: { xs: 70, sm: 100 }, px: 1 }}
                          >
                            <Tooltip title={cfg.fullLabel} arrow>
                              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                <Box
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    background: cfg.gradient,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    flexShrink: 0,
                                  }}
                                >
                                  {cfg.icon}
                                </Box>
                                <Typography variant="caption" fontWeight={700}>
                                  {cfg.label}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, width: { xs: 90, sm: 120 }, px: 1 }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.days.map((day) => {
                      const completedCount = day.sections.filter((s) => s.learningStatus === "completed").length;
                      const totalCount = day.sections.length;
                      const allCompleted = completedCount === totalCount;

                      return (
                        <TableRow
                          key={day.exerciseDay}
                          sx={{
                            "&:hover": { backgroundColor: "#f9f9f9" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={{ py: 1.5, px: { xs: 1, sm: 2 } }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "flex-start" },
                                gap: { xs: 0.5, sm: 1 },
                              }}
                            >
                              <Chip
                                label={`Day ${day.dayNumber}`}
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 600, flexShrink: 0 }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                  lineHeight: 1.4,
                                  fontSize: { xs: "0.78rem", sm: "0.875rem" },
                                }}
                              >
                                {day.exerciseDayTitle}
                              </Typography>
                            </Box>
                          </TableCell>
                          {SECTION_ORDER.map((type) => (
                            <TableCell key={type} align="center" sx={{ px: 1 }}>
                              <SectionCell
                                section={getSection(day, type)}
                                onReview={(sessionId) => setReviewSessionId(sessionId)}
                              />
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ px: 1 }}>
                            {allCompleted ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Completed"
                                size="small"
                                sx={{
                                  backgroundColor: "#e8f5e9",
                                  color: "#2e7d32",
                                  fontWeight: 600,
                                  "& .MuiChip-icon": { color: "#2e7d32" },
                                }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label={`${completedCount}/${totalCount}`}
                                sx={{
                                  backgroundColor: completedCount > 0 ? "#fff3e0" : "#f5f5f5",
                                  color: completedCount > 0 ? "#e65100" : "#9e9e9e",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))
      )}

      {/* Review Answers Dialog */}
      <ReviewAnswersDialog
        open={!!reviewSessionId}
        onClose={() => setReviewSessionId(null)}
        sessionId={reviewSessionId}
      />
    </Box>
  );
};

export default ExerciseStatistics;
